import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"
import gsap from "gsap"

export default class extends Controller {
  connect() {
    this.initThree()
    this.animate()
    window.addEventListener('resize', this.onWindowResize.bind(this))
    document.addEventListener('mousemove', this.onMouseMove.bind(this))
  }

  disconnect() {
    if (this.renderer) {
      window.removeEventListener('resize', this.onWindowResize.bind(this))
      document.removeEventListener('mousemove', this.onMouseMove.bind(this))
      this.renderer.dispose()
      this.element.innerHTML = ''
    }
  }

  initThree() {
    this.scene = new THREE.Scene()
    
    // Camera setup
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000)
    this.camera.position.z = 30

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.element.appendChild(this.renderer.domElement)

    // Particles (Nodes)
    const geometry = new THREE.BufferGeometry()
    const particlesCount = 150
    const posArray = new Float32Array(particlesCount * 3)

    for(let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 60 // Spread
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3))

    // Material
    this.material = new THREE.PointsMaterial({
      size: 0.2,
      color: 0x6366f1, // Indigo-500
      transparent: true,
      opacity: 0.8,
    })

    this.particlesMesh = new THREE.Points(geometry, this.material)
    this.scene.add(this.particlesMesh)

    // Connecting Lines
    this.linesMaterial = new THREE.LineBasicMaterial({
      color: 0x6366f1,
      transparent: true,
      opacity: 0.15
    })
    
    // Geometric shape in the center (Icosahedron) representing "Core / Structure"
    const icoGeometry = new THREE.IcosahedronGeometry(10, 1)
    const icoMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x8b5cf6, // Violet-500
      wireframe: true,
      transparent: true,
      opacity: 0.3
    })
    this.icoMesh = new THREE.Mesh(icoGeometry, icoMaterial)
    this.scene.add(this.icoMesh)

    this.mouseX = 0
    this.mouseY = 0
  }

  onMouseMove(event) {
    this.mouseX = event.clientX
    this.mouseY = event.clientY
  }

  onWindowResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }

  animate() {
    requestAnimationFrame(this.animate.bind(this))

    const time = Date.now() * 0.001

    // Rotation
    if (this.particlesMesh) this.particlesMesh.rotation.y = time * 0.05
    if (this.icoMesh) {
      this.icoMesh.rotation.x = time * 0.1
      this.icoMesh.rotation.y = time * 0.1
    }

    // Mouse Interaction (Parallax)
    this.camera.position.x += (this.mouseX * 0.01 - this.camera.position.x) * 0.05
    this.camera.position.y += (-this.mouseY * 0.01 - this.camera.position.y) * 0.05
    this.camera.lookAt(this.scene.position)

    // Color shift based on theme (check HTML class)
    const isDark = document.documentElement.classList.contains('dark')
    const targetColor = isDark ? new THREE.Color(0x818cf8) : new THREE.Color(0x4f46e5) // Light vs Dark indigo
    
    // Smoothly interpolate color
    if (this.material) this.material.color.lerp(targetColor, 0.05)
    if (this.linesMaterial) this.linesMaterial.color.lerp(targetColor, 0.05)

    this.renderer.render(this.scene, this.camera)
  }
}
